class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.cart = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.soldOutMessage = this.form.dataset.soldOutMessage;

    if(this.cart && this.submitButton) {
      this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    this.hideErrors = this.dataset.hideErrors === 'true';

    this.setupForm();
  }

  setupForm() {
    this.form.classList.add('group');
    this.form.setAttribute('data-state', 'waiting');
    this.form.querySelector('[name=id]').disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onSubmitHandler(e) {
    e.preventDefault();
    const formData = new FormData(this.form);
    this.form.setAttribute('data-state', 'loading');

    if(this.cart) {
      formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }

    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        Accept: `application/javascript`,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then((response) => response.json())
    .then((response) => {
      if(response.status) {
        if(this.soldOutMessage) {
          this.submitButton.querySelector('span').text = this.soldOutMessage;
        }
        this.handleErrorMessage(response.description);
      }
      else if(!this.cart) {
        window.location = window.routes.cart_url;
      }
      else {
        this.cart.renderContents(response);
        this.cart.drawer?.open();
      }
    })
    .catch((e) => {
      console.error(e);
    })
    .finally(() => {
      this.form.setAttribute('data-state', 'waiting');
    })
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;
    this.errorMessage = this.errorMessage || this.querySelector('.product-form-error-message');

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
    else {
      this.errorMessage.textContent = '';
    }
  }
}

customElements.define('product-form', ProductForm);

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.blockId = this.closest('.block').getAttribute('id');
    this.productId = this.dataset.productId;
    this.productGrouping = this.closest('.product-grouping');
    this.sectionId = this.dataset.section;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.enableLoading();
    this.disableButton();
    this.updateOptions();
    this.updateMasterId();
    this.removeErrorMessage();

    if(this.currentVariant) {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
    }
    else {
      this.updateButtonMessage(this.productForm.dataset.soldOutMessage);
      this.disableLoading();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
    const media = document.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    if(!media) return;
    const carousel = document.querySelector(`[data-carousel-id="${media.dataset.carouselJump}"]`);
    if(!carousel) return;
    carousel.jumpTo(media.dataset.jumpTo);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false' || this.productForm?.dataset?.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForm = this.productForm;
    if(!productForm) return;
    const input = productForm.querySelector('input[name="id"]');
    input.value = this.currentVariant.id;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    fetch(`${this.dataset.url}?variant=${requestedVariantId}&product=${this.productId}&section_id=${this.sectionId}`)
    .then(response => response.text())
    .then(response => {
      this.priceContainer?.setAttribute('data-state', 'waiting');
      if (this.currentVariant.id !== requestedVariantId) return;
      const html = new DOMParser().parseFromString(response, 'text/html');
      const source = html.querySelector(`product-price[data-product-id="${this.productId}"]`);

      if(source && this.priceContainer) this.priceContainer.innerHTML = source.innerHTML;
      this.priceContainer.querySelectorAll('[data-replace-price-class]').forEach(i => {
        i.setAttribute('class', this.priceContainer.getAttribute('data-price-class'));
      })
      if(this.currentVariant.available) {
        this.enableButton();
        this.updateButtonMessage(this.productForm.dataset.addMessage);
      }
      else {
        this.updateButtonMessage(this.productForm.dataset.soldOutMessage);
        this.disableButton();
      }
      this.disableLoading();
    })
  }

  updateButtonMessage(message) {
    const buttonText = this.formButton?.querySelector('.status-message');
    if(!buttonText) return;
    buttonText.textContent = message;
  }

  get productForm() {
    if(this.productGrouping) {
      return this.productGrouping.querySelector('product-form');
    }
    return this.closest('product-form') ?? document.querySelector(`product-form[data-section-id=${this.sectionId}]`);
  }

  get formButton() {
    return this.productForm?.querySelector('[name="add"]');
  }

  get priceContainer() {
    if(this.productGrouping) {
      return this.productGrouping.querySelector('product-price');
    }
    return document.querySelector(`product-price[data-section-id=${this.sectionId}]`);
  }

  removeErrorMessage() {
    const productForm = this.productForm;
    if(productForm) {
      productForm.handleErrorMessage();
    }
  }

  disableButton() {
    this.formButton?.setAttribute('disabled', 'disabled');
  }

  enableButton() {
    this.formButton?.removeAttribute('disabled');
  }

  enableLoading() {
    const productForm = this.productForm;
    if (!productForm) return;
    this.priceContainer?.setAttribute('data-state', 'loading');
    productForm.setAttribute('data-state', 'loading');
  }

  disableLoading() {
    const productForm = this.productForm;
    if (!productForm) return;
    this.priceContainer?.setAttribute('data-state', 'waiting');
    productForm.setAttribute('data-state', 'waiting');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('[data-fieldset]'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);